import { useState, useCallback } from 'react';
import { uploadTempFiles } from '../services/fileService';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_FILES = 5;

export const useTempFiles = () => {
  const [attachedFiles, setAttachedFiles] = useState([]);

  const handleFilesAttach = useCallback(async (files, sessionId = null) => {
    const validFiles = [];
    const errors = [];

    const currentFileCount = attachedFiles.length;
    
    if (currentFileCount >= MAX_FILES) {
      alert(`Maximum ${MAX_FILES} files allowed at a time`);
      return;
    }

    const remainingSlots = MAX_FILES - currentFileCount;
    const filesToProcess = files.slice(0, remainingSlots);

    filesToProcess.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size exceeds 20MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (filesToProcess.length < files.length) {
      alert(`Only ${remainingSlots} files can be added. Maximum ${MAX_FILES} files allowed.`);
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length === 0) return;

    const tempFileObjects = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      originalName: file.name,
      size: file.size,
      status: sessionId ? 'uploading' : 'pending',
      file: file
    }));

    setAttachedFiles(prev => [...prev, ...tempFileObjects]);

    if (sessionId) {
      console.log('[DEBUG] Uploading files immediately with session ID:', sessionId);
      try {
        const response = await uploadTempFiles(validFiles, sessionId);
        console.log('[DEBUG] Upload response:', response);

        if (response?.data && Array.isArray(response.data)) {
          tempFileObjects.forEach((fileObj, index) => {
            const serverFileData = response.data[index];
            if (serverFileData?.filename) {
              console.log('[DEBUG] File uploaded successfully:', serverFileData.filename);
              setAttachedFiles(prev =>
                prev.map(f =>
                  f.id === fileObj.id
                    ? { ...f, serverFileName: serverFileData.filename, status: 'ready' }
                    : f
                )
              );
            }
          });
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        tempFileObjects.forEach(fileObj => {
          setAttachedFiles(prev =>
            prev.map(f =>
              f.id === fileObj.id
                ? { ...f, status: 'error', error: error.message || 'Upload failed' }
                : f
            )
          );
        });
      }
    }
  }, [attachedFiles]);

  const uploadFiles = useCallback(async (sessionId) => {
    const filesToUpload = attachedFiles.filter(f => f.status === 'pending');
    
    if (filesToUpload.length === 0) {
      return [];
    }

    setAttachedFiles(prev => 
      prev.map(f => f.status === 'pending' ? { ...f, status: 'uploading' } : f)
    );

    try {
      const fileObjects = filesToUpload.map(f => f.file);
      const response = await uploadTempFiles(fileObjects, sessionId);

      if (response?.data && Array.isArray(response.data)) {
        filesToUpload.forEach((fileObj, index) => {
          const serverFileData = response.data[index];
          if (serverFileData?.filename) {
            setAttachedFiles(prev =>
              prev.map(f =>
                f.id === fileObj.id
                  ? { ...f, serverFileName: serverFileData.filename, status: 'ready' }
                  : f
              )
            );
          }
        });
        
        return response.data.map(d => d.filename).filter(Boolean);
      }
      
      return [];
    } catch (error) {
      console.error('Error uploading files:', error);
      filesToUpload.forEach(fileObj => {
        setAttachedFiles(prev =>
          prev.map(f =>
            f.id === fileObj.id
              ? { ...f, status: 'error', error: error.message || 'Upload failed' }
              : f
          )
        );
      });
      throw error;
    }
  }, [attachedFiles]);

  const handleFileRemove = useCallback((fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const clearAttachedFiles = useCallback(() => {
    setAttachedFiles([]);
  }, []);

  const getReadyFileNames = useCallback(() => {
    return attachedFiles
      .filter(f => f.status === 'ready' && f.serverFileName)
      .map(f => f.serverFileName);
  }, [attachedFiles]);

  const hasUploadingFiles = useCallback(() => {
    return attachedFiles.some(f => f.status === 'uploading');
  }, [attachedFiles]);

  const hasPendingFiles = useCallback(() => {
    return attachedFiles.some(f => f.status === 'pending');
  }, [attachedFiles]);

  return {
    attachedFiles,
    handleFilesAttach,
    handleFileRemove,
    clearAttachedFiles,
    getReadyFileNames,
    hasUploadingFiles,
    hasPendingFiles,
    uploadFiles
  };
};

